import React from 'react';
import {
  formatMetric,
  getResultMetric,
  getStatusTone,
  hasNumber,
  localizedValue
} from './nationalizationViewModel';

export default function NationalizationBreakdown({ overview, language, t }) {
  const dimension = overview?.breakdown_dimension || {};
  const breakdowns = Array.isArray(overview?.breakdowns) ? overview.breakdowns : [];
  // Keep a maximum of three cards per line and rebalance a four-card tail as
  // two-by-two. Each row owns its columns, so a final pair expands evenly
  // instead of leaving an awkward empty third column on wide screens.
  const rows = [];
  for (let index = 0; index < breakdowns.length;) {
    const remaining = breakdowns.length - index;
    const rowSize = remaining === 4 ? 2 : Math.min(3, remaining);
    rows.push(breakdowns.slice(index, index + rowSize));
    index += rowSize;
  }
  const dimensionLabel = localizedValue(dimension, language) || t(
    `breakdownTypes.${dimension?.code || 'legalRequirements'}`,
    'Legal requirements'
  );
  // An empty result is not the same thing as a legal exemption. Country
  // adapters marked as incomplete deliberately return `coverage_unknown` so
  // the screen asks for regulator verification instead of claiming N/A.
  const explicitlyNotApplicable = overview?.current?.status_code === 'not_applicable';
  const coverageUnknown = !explicitlyNotApplicable && (
    dimension?.code === 'coverage_unknown'
    || (overview?.coverage?.status_code && overview.coverage.status_code !== 'complete')
  );

  return (
    <section className="nationalization-breakdown" aria-labelledby="nationalization-breakdown-title">
      <h3 id="nationalization-breakdown-title" className="nationalization-breakdown-heading">
        {t('page.breakdownHeading', 'Mandate Thresholds - {{dimension}}', { dimension: dimensionLabel })}
      </h3>

      {breakdowns.length ? (
        <div className="nationalization-breakdown-grid">
          {rows.map((row, rowIndex) => (
            <div className="nationalization-breakdown-row" key={`breakdown-row-${rowIndex}`}>
              {row.map((item) => {
            const metric = getResultMetric(item);
            const explicitTone = getStatusTone(item?.status_code);
            // Keep the requested red/green target comparison even when the
            // calculation is separately labelled provisional for missing evidence.
            const tone = hasNumber(metric.current) && hasNumber(metric.required)
              ? Number(metric.current) >= Number(metric.required) ? 'success' : 'danger'
              : explicitTone;
            const itemDimensionLabel = localizedValue(item?.dimension, language) || dimensionLabel;
            const requiredLabel = metric.resultType === 'count'
              ? t('page.requiredCountValue', 'Required: {{value}} qualifying employees', {
                value: formatMetric(metric.required, metric.suffix, 1, language)
              })
              : metric.resultType === 'band'
                ? t('page.requiredBand', 'Required classification: {{value}}', {
                  value: formatMetric(metric.required, metric.suffix, 1, language)
                })
                : t('page.requiredValue', 'Required: at least {{value}}', {
                  value: formatMetric(metric.required, metric.suffix, 1, language)
                });
            return (
              <article key={item.code || localizedValue(item, 'en')} className="nationalization-breakdown-card">
                <h4>{localizedValue(item, language) || item.code}</h4>
                <strong className={`nationalization-tone-${tone}`}>
                  {formatMetric(metric.current, metric.suffix, 1, language)}
                </strong>
                <span>{itemDimensionLabel}</span>
                <p className={`nationalization-tone-${tone}`}>
                  {metric.required !== null && metric.required !== undefined
                    ? requiredLabel
                    : t('resultStates.targetUnavailableShort', 'Target unavailable')}
                </p>
              </article>
            );
              })}
            </div>
          ))}
        </div>
      ) : (
        <p className="nationalization-no-breakdown">
          {coverageUnknown
            ? t(
              'resultStates.coverageUnknownBreakdown',
              'Requirements may apply, but this beta catalogue is not complete. Verify this establishment with the regulator.'
            )
            : explicitlyNotApplicable
              ? t('resultStates.notApplicableBreakdown', 'No separate breakdown applies to this verified exemption.')
              : t('resultStates.noBreakdown', 'This requirement does not use a separate {{dimension}} breakdown.', {
                dimension: dimensionLabel
              })}
        </p>
      )}
    </section>
  );
}
